import React, { useEffect, useRef, useState } from 'react';
import { loadSvg } from '../utils/loadSvg';
import { fetchTeacherProfileImage } from '../hooks/useTeacherProfileImage';
import ControlledGif from './ControlledGif';
import gif2 from '../ui/Insegnante.gif';
import Insegnante from '../ui/Insegnante.png';
import { ReactComponent as Blob2 } from '../ui/Blob2.svg';

const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? '';

async function publicGet(path: string) {
  const res = await fetch(`${BASE_URL}/api${path}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

const toCamelCase = (name: string): string => {
  const nameWithSpaces = name.replace(/['\-&\/]/g, ' ');
  return nameWithSpaces
    .replace(/[^\w\s]/gi, '')
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .map((word, index) => {
      if (index === 0) return word.toLowerCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
};

interface SubjectItem {
  id: number;
  name: string;
  icon: string;
}

interface TeacherCard {
  id: number;
  firstName: string;
  lastName: string;
  bio: string;
  profileImage: string;
  subjects: SubjectItem[];
}

export default function ReviewsCarousel() {
  const [teachers, setTeachers] = useState<TeacherCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [carouselReady, setCarouselReady] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>();
  const translateXRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const singleSetWidthRef = useRef<number>(0);

  useEffect(() => {
    const validCards: TeacherCard[] = [];
    let cancelled = false;

    const processTeacher = async (t: any) => {
      if (!t.bio) return;
      const profileImage = (await fetchTeacherProfileImage(t.id, 'large')) ?? '';
      if (!profileImage) return;

      const subjects: SubjectItem[] = [];
      for (const s of (t.subjects ?? []).slice(0, 6)) {
        const name: string = s.subject?.name ?? '';
        if (!name) continue;
        const icon = await loadSvg(toCamelCase(name));
        subjects.push({ id: s.subject?.id ?? 0, name, icon });
      }

      validCards.push({
        id: t.id,
        firstName: t.first_name ?? '',
        lastName: t.last_name ?? '',
        bio: t.bio,
        profileImage,
        subjects,
      });

      if (cancelled) return;

      // Appena 3 validi: mostra il carosello subito
      if (validCards.length === 3) {
        setLoading(false);
        setCarouselReady(true);
      }

      // Aggiorna i teacher ad ogni nuovo valido trovato
      setTeachers([...validCards]);
    };

    const fetchTeachers = async () => {
      try {
        const MAX_VALID = 10;
        const firstData = await publicGet('/teachers?page_size=10');
        const totalPages: number = firstData.pagination?.total_pages ?? 1;

        for (const t of firstData.teachers ?? []) {
          if (cancelled || validCards.length >= MAX_VALID) break;
          await processTeacher(t);
        }

        for (let page = 2; page <= totalPages && !cancelled && validCards.length < MAX_VALID; page++) {
          const pageData = await publicGet(`/teachers?page=${page}&page_size=10`);
          for (const t of pageData.teachers ?? []) {
            if (cancelled || validCards.length >= MAX_VALID) break;
            await processTeacher(t);
          }
        }
      } catch (err) {
        console.error('Errore nel caricamento degli insegnanti:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTeachers();
    return () => { cancelled = true; };
  }, []);

  // Fade-in del contenuto quando il loading termina
  useEffect(() => {
    if (!loading) {
      const t = setTimeout(() => setContentVisible(true), 50);
      return () => clearTimeout(t);
    }
  }, [loading]);

  // Avvia l'animazione una sola volta quando il carosello è pronto
  useEffect(() => {
    if (!carouselReady) return;
    const container = containerRef.current;
    if (!container) return;

    setTimeout(() => {
      singleSetWidthRef.current = container.scrollWidth / 5;
    }, 100);

    const speed = 40;

    const animate = (currentTime: number) => {
      if (!lastTimeRef.current) lastTimeRef.current = currentTime;
      const deltaTime = Math.min((currentTime - lastTimeRef.current) / 1000, 0.1);
      lastTimeRef.current = currentTime;

      translateXRef.current += speed * deltaTime;
      if (singleSetWidthRef.current > 0) {
        translateXRef.current = translateXRef.current % singleSetWidthRef.current;
      }
      if (container) {
        container.style.transform = `translateX(-${translateXRef.current}px)`;
      }
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [carouselReady]);

  // Ricalcola la larghezza quando vengono aggiunti teacher, senza riavviare l'animazione
  useEffect(() => {
    if (!carouselReady || !containerRef.current) return;
    const container = containerRef.current;
    const timer = setTimeout(() => {
      singleSetWidthRef.current = container.scrollWidth / 5;
    }, 50);
    return () => clearTimeout(timer);
  }, [teachers]);

  if (loading) {
    return (
      <div className="w-full py-8 flex justify-center items-center">
        <span className="text-gray-400 text-sm">Caricamento insegnanti...</span>
      </div>
    );
  }

  if (teachers.length < 3) {
    return (
      <div className={`relative items-center justify-center flex flex-col sm:flex w-full max-w-xs sm:max-w-md mx-auto sm:mx-0 transition-all duration-1000 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <Blob2 className="w-full h-full max-w-full" />
        <div className="absolute flex justify-center items-center z-10 inset-0">
          <ControlledGif
            gifSrc={gif2}
            staticSrc={Insegnante}
            delay={400}
            preload={true}
            alt="insegnante"
            className="w-3/4 h-3/4 max-w-full max-h-full object-contain"
          />
        </div>
      </div>
    );
  }

  const multiplied = Array(5).fill(teachers).flat() as TeacherCard[];

  return (
    <div className={`relative w-full overflow-hidden py-4 h-full items-center flex transition-all duration-1000 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
      {/* Gradient overlays */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-fpcwhite to-transparent" />
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-fpcwhite to-transparent" />
      </div>

      <div ref={containerRef} className="flex" style={{ willChange: 'transform', width: 'fit-content' }}>
        {multiplied.map((teacher, index) => (
          <div
            key={`${teacher.id}-${index}`}
            className="flex-shrink-0 mx-3 w-64 bg-fpcpink rounded-2xl shadow-md border-2 border-opacity-50 border-fpcred overflow-hidden flex flex-col">
            {/* Immagine cover con sfumatura verso il basso */}
            <div className="relative h-48 md:h-64 w-64 flex-shrink-0">
              <img
                src={teacher.profileImage}
                alt={`${teacher.firstName}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 h-8 md:h-16 bg-gradient-to-b from-transparent to-fpcpink" />
            </div>

            {/* Bio */}
            <div className="px-3 pb-2">
              <span className="font-bold text-fpcred text-2xl drop-shadow-sm">{teacher.firstName}</span>
              <p className="text-md text-gray-500 line-clamp-4 leading-relaxed">{teacher.bio}</p>
            </div>

            {/* Materie */}
            {teacher.subjects.length > 0 && (
              <div className="px-3 pb-3 mt-auto flex flex-wrap gap-1.5">
                {teacher.subjects.map((subject) =>
                  subject.icon ? (
                    <img
                      key={subject.id}
                      src={subject.icon}
                      alt={subject.name}
                      title={subject.name}
                      className="w-8 h-8 object-contain"
                    />
                  ) : (
                    <span key={subject.id} className="text-xs text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
                      {subject.name}
                    </span>
                  ),
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
